// A staff note with formatting (bold, italic, lists, links: the Formatting.tsx subset). It shows formatted, and turns
// into a text box with the formatting bar while it is edited (an empty note starts in the box). Saving is the caller's.
import { useRef, useState, type ReactNode } from 'react';
import { Button, TextArea } from '../ui';
import { MessageBody, useFormatting } from './feedback/Formatting';

export function FormattedNote({ label, name, value, onChange, onDone, placeholder, maxLength, autoFocus, rows = 2 }: {
  /** Shown above the note; it names the text box too. */
  label: ReactNode;
  /** What the note is, for the Edit button's name ("internal note for Flags"). */
  name: string;
  value: string; onChange: (text: string) => void;
  /** Focus left the note (the box and its bar). */
  onDone?: () => void;
  placeholder?: string; maxLength?: number; autoFocus?: boolean; rows?: number;
}) {
  const box = useRef<HTMLTextAreaElement>(null);
  const wrap = useRef<HTMLDivElement>(null);
  const [editing, setEditing] = useState(!!autoFocus || !value.trim());
  const formatting = useFormatting(box, onChange);

  if (!editing && value.trim()) {
    return (
      <div className="flex flex-col gap-1">
        <div className="text-[13px] font-semibold text-ink-2">{label}</div>
        <MessageBody text={value} className="rounded-[10px] border border-line bg-row-hi px-3 py-2.5 text-sm" />
        <Button variant="link" size="sm" className="self-start" aria-label={`Edit ${name}`}
          onClick={() => { setEditing(true); requestAnimationFrame(() => box.current?.focus()); }}>
          Edit
        </Button>
      </div>
    );
  }
  return (
    <div ref={wrap} className="flex flex-col gap-1.5" onBlur={(e) => {
      if (wrap.current?.contains(e.relatedTarget as Node | null)) return;
      onDone?.();
      if (value.trim()) setEditing(false);
    }}>
      <TextArea ref={box} small label={label} autoFocus={autoFocus} maxLength={maxLength} placeholder={placeholder} rows={rows}
        value={value} onChange={(e) => onChange(e.target.value)} onKeyDown={formatting.onKeyDown} />
      {formatting.bar}
    </div>
  );
}
